import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type { Service } from 'webapp-types';
import { Theme } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import themed from 'webapp/decorators/themed';
import ColorBlockComponent from 'webapp/components/color-block';
import CommonPopupsPopupColorsAndThemesComponent from 'webapp/components/common/popups/popup-colors-and-themes';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type ReportSlide from 'webapp/models-custom/report-slide';

interface Args {
	selectedColor: string;
	selectedTheme: number;
	reportSlide: ReportSlide | null;
	onColorChange: (color: string) => void;
	onThemeChange: (theme: number) => void;
	theme?: Theme | string;
	allowCustom?: boolean;
}

@themed()
export default class CommonColorsAndThemesComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;

	declare themeClass: string; // from @themed

	@action
	openColorPopup(e: PointerEvent) {
		const attachTo = e.target as HTMLElement;

		if (!attachTo) {
			return;
		}

		const isPopupOpen = this.popupService.isOpen(CommonPopupsPopupColorsAndThemesComponent);

		if (!isPopupOpen) {
			const { selectedColor: color, selectedTheme: theme, reportSlide } = this.args;

			const popup = this.popupService.createContext(
				CommonPopupsPopupColorsAndThemesComponent,
				attachTo,
				{
					popupClass: 'popup-type-color',
					theme: Theme.DARK,
					allowStacked: true,
				},
			);

			popup.open({
				color,
				theme,
				reportSlide,
				onColorChange: (color: string) => {
					this.analyticsService.event(TrackingEvent.DESIGNER_COLOR_CHANGE);
					this.args.onColorChange(color);
				},
				onThemeChange: (theme: number) => {
					this.analyticsService.event(TrackingEvent.DESIGNER_THEME_CHANGE);
					this.args.onThemeChange(theme);
				},
				allowCustom: this.args.allowCustom,
			});
		}
	}
	<template>
		<div id="boardColor" class={{this.themeClass}}>
			<a role="button" {{on "click" this.openColorPopup}}>
				<ColorBlockComponent @color={{@selectedColor}} />
				<CommonUiIconComponent @name="ChevronDown" @size="xs" @class="board-color-arrow" />
			</a>
		</div>
	</template>
}
